export type StockTaxTrade = Record<string, string | number>;

export type StockTaxSummaries = {
  intraday: { totalGainLoss: number };
  shortTerm: { totalGainLoss: number };
  longTerm: { totalGainLoss: number };
};

export type StockTaxMemberData = {
  memberName: string;
  intradayTrades: StockTaxTrade[];
  shortTermTrades: StockTaxTrade[];
  longTermTrades: StockTaxTrade[];
  summaries: StockTaxSummaries;
};

export type ParsedStockTaxReport = {
  members: StockTaxMemberData[];
};

export const STOCK_TAX_INTRADAY_REPORT = "Intraday";
export const STOCK_TAX_SHORT_TERM_REPORT = "Short Term Capital Gain";
export const STOCK_TAX_LONG_TERM_REPORT = "Long Term Capital Gain";

export const STOCK_TAX_REPORT_TYPES = new Set([
  STOCK_TAX_INTRADAY_REPORT,
  STOCK_TAX_SHORT_TERM_REPORT,
  STOCK_TAX_LONG_TERM_REPORT,
]);

export const isStockTaxReportType = (reportType?: string): boolean =>
  STOCK_TAX_REPORT_TYPES.has(String(reportType ?? "").trim());

/** Intraday / STCG / LTCG — same date range UI and timestamp payload as unrealized. */
export const isCapitalGainReportWithDateRange = (reportType?: string): boolean =>
  isStockTaxReportType(reportType);

export const STOCK_TAX_REPORT_COLUMNS: Array<{
  key: string;
  value: string;
  locked?: boolean;
}> = [
  { key: "Asset Name", value: "Asset Name", locked: true },
  { key: "Qty Sold", value: "Qty Sold" },
  { key: "Sale Price", value: "Sale Price" },
  { key: "Purchase Price", value: "Purchase Price" },
  { key: "Gain/Loss", value: "Gain/Loss" },
  { key: "Holding Period (Days)", value: "Holding Period (Days)" },
];

const toTradeArray = (value: unknown): StockTaxTrade[] =>
  Array.isArray(value) ? (value as StockTaxTrade[]) : [];

const readSummaryTotal = (summaries: unknown, key: string): number => {
  if (!summaries || typeof summaries !== "object") return 0;
  const bucket = (summaries as Record<string, unknown>)[key];
  if (!bucket || typeof bucket !== "object") return 0;
  const total = Number((bucket as Record<string, unknown>).totalGainLoss);
  return Number.isFinite(total) ? total : 0;
};

const parseMemberBlock = (
  memberName: string,
  block: Record<string, unknown>,
): StockTaxMemberData => ({
  memberName,
  intradayTrades: toTradeArray(block.intradayTrades),
  shortTermTrades: toTradeArray(block.shortTermTrades),
  longTermTrades: toTradeArray(block.longTermTrades),
  summaries: {
    intraday: { totalGainLoss: readSummaryTotal(block.summaries, "intraday") },
    shortTerm: { totalGainLoss: readSummaryTotal(block.summaries, "shortTerm") },
    longTerm: { totalGainLoss: readSummaryTotal(block.summaries, "longTerm") },
  },
});

const isMemberTaxBlock = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const block = value as Record<string, unknown>;
  return (
    "intradayTrades" in block ||
    "shortTermTrades" in block ||
    "longTermTrades" in block ||
    "summaries" in block
  );
};

export const parseStockTaxReport = (
  payload: unknown,
): ParsedStockTaxReport | null => {
  if (!payload || typeof payload !== "object") return null;

  const root = payload as Record<string, unknown>;
  const response = root.response ?? root.data ?? root;

  if (!response || typeof response !== "object" || Array.isArray(response)) {
    return null;
  }

  const responseRecord = response as Record<string, unknown>;

  if (isMemberTaxBlock(responseRecord)) {
    return {
      members: [parseMemberBlock("Report", responseRecord)],
    };
  }

  const members = Object.entries(responseRecord)
    .filter(([, value]) => isMemberTaxBlock(value))
    .map(([memberName, value]) =>
      parseMemberBlock(memberName, value as Record<string, unknown>),
    );

  return members.length > 0 ? { members } : null;
};

const getTradesForReportType = (
  member: StockTaxMemberData,
  reportType: string,
): StockTaxTrade[] => {
  switch (reportType) {
    case STOCK_TAX_INTRADAY_REPORT:
      return member.intradayTrades;
    case STOCK_TAX_SHORT_TERM_REPORT:
      return member.shortTermTrades;
    case STOCK_TAX_LONG_TERM_REPORT:
      return member.longTermTrades;
    default:
      return [];
  }
};

const getSummaryKeyForReportType = (
  reportType: string,
): keyof StockTaxSummaries | null => {
  switch (reportType) {
    case STOCK_TAX_INTRADAY_REPORT:
      return "intraday";
    case STOCK_TAX_SHORT_TERM_REPORT:
      return "shortTerm";
    case STOCK_TAX_LONG_TERM_REPORT:
      return "longTerm";
    default:
      return null;
  }
};

export const extractStockTaxSummaryTotal = (
  parsed: ParsedStockTaxReport | null,
  reportType: string,
): number | null => {
  if (!parsed) return null;
  const summaryKey = getSummaryKeyForReportType(reportType);
  if (!summaryKey) return null;

  let total = 0;
  let hasSummary = false;

  for (const member of parsed.members) {
    const memberTotal = member.summaries?.[summaryKey]?.totalGainLoss;
    if (Number.isFinite(memberTotal)) {
      total += memberTotal;
      hasSummary = true;
    }
  }

  return hasSummary ? total : null;
};

export const extractStockTaxRows = (
  parsed: ParsedStockTaxReport,
  reportType: string,
): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];

  for (const member of parsed.members) {
    const trades = getTradesForReportType(member, reportType);
    for (const trade of trades) {
      const { Type: _type, type: _typeLower, ...rest } = trade as Record<
        string,
        unknown
      >;
      rows.push({
        ...rest,
        ...(member.memberName !== "Report"
          ? { memberName: member.memberName }
          : {}),
      });
    }
  }

  return rows;
};
